import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Icon,
  NumericInput,
  TextSwitch,
  Theme,
  Tooltip,
  withSkeletonLoading,
} from "@landtechnologies/components";
import { Stores, useGlobalState } from "react-migration/lib/persistence/state";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { MeasurementSystem } from "src/js/stores/user/store";
import logSiteCardEvent from "src/js/view/sites/logSiteCardEvent";
import classNames from "classnames";
import {
  Currency,
  formatToCurrency,
  formatToPrecision,
  getCurrencyFromLicense,
} from "react-migration/lib";
import { i18n } from "src/js/i18n";
import { SupportedLocale } from "src/js/util/dates";
import { mToMiles, milesToM } from "src/js/util/units_and_constants";
import { IComparablesTransactionDTO } from "react-migration/domains/comparables";
import { Search } from "./Search";
import { hasFeature } from "src/js/stores/user/actions";
import { Feature } from "src/js/stores/user";
import { calculateAveragePricePerUnitArea } from "react-migration/domains/sites/helpers/calculateAveragePricePerUnitArea";

export interface OverviewProps {
  loading: boolean;
  maxDistanceMeters: number;
  onMaxDistanceMetersChange: (maxDistanceMeters: number) => void;
  search: IComparablesTransactionDTO[];
}

export const Overview = withSkeletonLoading<OverviewProps>(
  ({ hideSkeletonLoading, loading, search, maxDistanceMeters, onMaxDistanceMetersChange }) => {
    const { t } = useTranslation();
    const [, { updateMeasurementSystemPreference, getMeasurementSystemPreference, saveUser }] =
      useGlobalState(Stores.user);
    const [{ priceMode }] = useGlobalState(Stores.comparables);

    const [sitesCount, setSitesCount] = useState(0);

    const unitType = getMeasurementSystemPreference();

    const radius = useMemo(() => {
      const radius =
        unitType === "imperial" ? mToMiles(maxDistanceMeters) : maxDistanceMeters / 1000;
      return formatToPrecision(radius);
    }, [maxDistanceMeters, unitType]);

    const pricePerUnitArea = useMemo(
      () => calculateAveragePricePerUnitArea(search, unitType, priceMode),
      [search, unitType, priceMode]
    );

    const showDataSourceTooltip = useMemo(() => {
      return !hasFeature(Feature.usAccess);
    }, []);

    const averageValue = useMemo(() => {
      const currency = getCurrencyFromLicense();
      if (loading || !pricePerUnitArea) {
        return t(getNaTextCode(currency));
      }
      return formatToCurrency({
        amount: pricePerUnitArea,
        currency,
        locale: i18n.locale as SupportedLocale,
      });
    }, [loading, t, pricePerUnitArea]);

    useEffect(() => {
      !loading && hideSkeletonLoading();
    }, [loading, hideSkeletonLoading]);

    useEffect(() => {
      if (search) {
        setSitesCount(search.length);
      }
    }, [search]);

    const onUnitChange = async (unit: MeasurementSystem) => {
      updateMeasurementSystemPreference(unit);
      await saveUser();
      logSiteCardEvent({
        name: "Site Card Comparables Tab - Unit Type Updated",
        context: { type: unit, source: "sitecard-lite" },
      });
    };

    const onRadiusChange = useCallback(
      (radius: number) => {
        const maxDistanceMeters = unitType === "imperial" ? milesToM(radius) : radius * 1000;
        onMaxDistanceMetersChange(maxDistanceMeters);
      },
      [unitType, onMaxDistanceMetersChange]
    );

    return (
      <div
        className={classNames(
          "atlas-p-4",
          "atlas-z-0",
          "atlas-bg-background-white",
          "atlas-border-b",
          "atlas-border-neutral-200",
          "atlas-gap-y-4",
          "atlas-flex",
          "atlas-flex-col"
        )}
        data-testid="comparables-overview"
      >
        <div
          data-testid="comparables-overview-header"
          className={classNames("atlas-flex", "atlas-justify-between", "atlas-content-center")}
        >
          <span
            className={classNames(
              "atlas-font-semibold",
              "atlas-text-xl",
              "atlas-text-content-primary"
            )}
          >
            {t("sites.card.comparables.overview")}
          </span>
          <TextSwitch<MeasurementSystem>
            value={unitType}
            name="unitType"
            label={t("sites.card.comparables.unit_type")}
            widthClass={"atlas-min-w-[140px]"}
            onChange={onUnitChange}
            options={[
              { label: t("sites.card.comparables.imperial"), value: MeasurementSystem.IMPERIAL },
              { label: t("sites.card.comparables.metric"), value: MeasurementSystem.METRIC },
            ]}
          />
        </div>
        <div
          data-testid="comparables-overview-body"
          className={classNames("atlas-flex", "atlas-flex-col")}
        >
          <div className={classNames("atlas-flex", "atlas-flex-row", "atlas-items-baseline")}>
            <span className={classNames("atlas-text-base", "atlas-font-semibold")}>
              {averageValue}
            </span>
            <span
              className={classNames(
                "atlas-ml-1",
                "atlas-text-base",
                "atlas-text-content-primary",
                "atlas-font-regular"
              )}
            >
              {unitType === MeasurementSystem.IMPERIAL
                ? t("sites.card.comparables.per_square_foor")
                : t("sites.card.comparables.per_meter_squared")}
            </span>
            {showDataSourceTooltip && (
              <Tooltip.Wrapper>
                <Tooltip.Trigger className="atlas-ml-1">
                  <Icon icon="icon-lt-question-line" size="sm" cursor="pointer" />
                </Tooltip.Trigger>
                <Tooltip.Content theme={Theme.Dark} side="top" align="center">
                  {t("sites.card.comparables_layer.ukhpi_information")}
                </Tooltip.Content>
              </Tooltip.Wrapper>
            )}
          </div>
          <SecondaryText sitesCount={sitesCount} pricePerUnitArea={pricePerUnitArea} />
        </div>
        <div
          className={classNames(
            "atlas-flex",
            "atlas-flex-row",
            "atlas-justify-between",
            "atlas-items-center"
          )}
          data-testid="comparables-overview-radius"
        >
          <span
            className={classNames(
              "atlas-text-content-primary",
              "atlas-font-semibold",
              "atlas-text-sm"
            )}
          >
            {t("sites.card.comparables.radius")}
          </span>
          <NumericInput
            data-testid="radius-filter"
            units={
              getMeasurementSystemPreference() === MeasurementSystem.IMPERIAL
                ? t("sites.card.comparables.miles")
                : t("sites.card.comparables.km")
            }
            className="atlas-w-1/2"
            value={radius}
            step={0.05}
            onChange={onRadiusChange}
          />
        </div>
        <div className="atlas-border-b atlas-border-border-divider-subtle" />
        <Search loading={loading} records={search} />
      </div>
    );
  }
);

function getNaTextCode(currency: Currency) {
  switch (currency) {
    case "USD":
      return "sites.card.comparables.usd_n_a";
    case "GBP":
    default:
      return "sites.card.comparables.gbp_n_a";
  }
}

interface SecondaryTextProps {
  pricePerUnitArea: number;
  sitesCount: number;
}

const SecondaryText = ({ pricePerUnitArea, sitesCount }: SecondaryTextProps) => {
  const { t } = useTranslation();
  return (
    <span
      className={classNames("atlas-text-content-secondary", "atlas-text-sm", "atlas-font-regular")}
    >
      {pricePerUnitArea > 0
        ? t("sites.card.comparables.this_value_is_average", { sitesCount })
        : t("sites.card.comparables_layer.no_floor_area_data_available")}
    </span>
  );
};
